import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Building2, MapPin, Phone, ArrowLeft, Save } from 'lucide-react'
import { api } from '../../../lib/api'
import toast from 'react-hot-toast'

const CreateStore = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    latitude: '',
    longitude: '',
    offersDelivery: true,
    offersKitchenService: false,
    deliveryFee: '50',
    minOrderAmount: '200'
  })

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }))
  }

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('La geolocalización no está disponible en este navegador. Ingresa las coordenadas manualmente.')
      return
    }

    // Verificar si estamos en un contexto seguro (HTTPS o localhost)
    const isSecureContext = window.location.protocol === 'https:' || 
                            window.location.hostname === 'localhost' || 
                            window.location.hostname === '127.0.0.1'

    if (!isSecureContext) {
      toast.error('La geolocalización requiere HTTPS o localhost. Por favor, ingresa las coordenadas manualmente.', {
        duration: 5000
      })
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData(prev => ({
          ...prev,
          latitude: position.coords.latitude.toString(),
          longitude: position.coords.longitude.toString()
        }))
        toast.success('Ubicación obtenida correctamente')
      },
      (error) => {
        console.error('Error getting location:', error)
        let errorMessage = 'No se pudo obtener la ubicación. '
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Permiso denegado. Por favor, permite el acceso a la ubicación o ingresa las coordenadas manualmente.'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'La información de ubicación no está disponible. Ingresa las coordenadas manualmente.'
            break
          case error.TIMEOUT:
            errorMessage += 'Tiempo de espera agotado. Ingresa las coordenadas manualmente.'
            break
          default:
            errorMessage += 'Ingresa las coordenadas manualmente.'
            break
        }
        
        toast.error(errorMessage, {
          duration: 5000
        })
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast.error('El nombre del negocio es obligatorio')
      return
    }

    if (!formData.address.trim()) {
      toast.error('La dirección es obligatoria')
      return
    }

    if (!formData.phone.trim()) {
      toast.error('El teléfono es obligatorio')
      return
    }

    // Validar coordenadas
    const latitude = formData.latitude ? parseFloat(formData.latitude) : null
    const longitude = formData.longitude ? parseFloat(formData.longitude) : null

    if (latitude === null || isNaN(latitude) || latitude < -90 || latitude > 90) {
      toast.error('Por favor, ingresa una latitud válida (entre -90 y 90) o usa el botón para obtener tu ubicación')
      return
    }

    if (longitude === null || isNaN(longitude) || longitude < -180 || longitude > 180) {
      toast.error('Por favor, ingresa una longitud válida (entre -180 y 180) o usa el botón para obtener tu ubicación')
      return
    }

    try {
      setLoading(true)

      const storeData = {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        address: formData.address.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || undefined,
        latitude: latitude,
        longitude: longitude,
        offersDelivery: formData.offersDelivery,
        offersKitchenService: formData.offersKitchenService,
        deliveryFee: parseFloat(formData.deliveryFee) || 0,
        minOrderAmount: parseFloat(formData.minOrderAmount) || 0
      }

      await api.createStore(storeData)

      toast.success('¡Negocio creado exitosamente! Tu solicitud será revisada por un administrador.')
      navigate('/store-owner/my-stores')

    } catch (error: any) {
      console.error('Error creating store:', error)
      
      // Mostrar errores de validación específicos
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors
        validationErrors.forEach((err: any) => {
          toast.error(`${err.param || 'Campo'}: ${err.msg || err.message || 'Error de validación'}`)
        })
      } else {
        toast.error(error.response?.data?.message || 'Error al crear el negocio. Verifica que todos los campos sean correctos.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pb-20 sm:pb-6">
      <div className="max-w-3xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Header - iMac Style */}
        <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-2xl shadow-lg border-b-2 border-gray-200/60 mb-4 sm:mb-6 -mx-3 sm:-mx-6 lg:-mx-8 px-3 sm:px-6 lg:px-8 py-3 sm:py-5">
          <div className="flex items-center">
            <button
              onClick={() => navigate('/store-owner/dashboard')}
              className="p-2 sm:p-2.5 rounded-xl hover:bg-gray-100/80 transition-all duration-200 active:scale-95 mr-3 sm:mr-4"
            >
              <ArrowLeft className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight mb-1">
                Crear Nuevo Negocio
              </h1>
              <p className="text-xs sm:text-base text-gray-600 font-medium">
                Registra tu negocio para comenzar a vender
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl sm:rounded-xl sm:rounded-2xl shadow-2xl border-2 border-gray-200/60 overflow-hidden">
          <form onSubmit={handleSubmit} className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
            {/* Basic Information */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl sm:rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-gray-200/50">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl sm:rounded-2xl flex items-center justify-center mr-3 shadow-lg">
                  <Building2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <span>Información Básica</span>
              </h2>

              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                    Nombre del Negocio *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md"
                    placeholder="Ej: Negocio María"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                    Descripción
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md resize-none"
                    placeholder="Describe tu negocio, especialidades, etc."
                  />
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl sm:rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-gray-200/50">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl sm:rounded-2xl flex items-center justify-center mr-3 shadow-lg">
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <span>Información de Contacto</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md"
                    placeholder="809-555-0123"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md"
                    placeholder="negocio@email.com"
                  />
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl sm:rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-gray-200/50">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl sm:rounded-2xl flex items-center justify-center mr-3 shadow-lg">
                  <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                </div>
                <span>Ubicación</span>
              </h2>

              <div className="space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                    Dirección *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md"
                    placeholder="Calle Principal #123, Santiago"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                      Latitud *
                    </label>
                    <input
                      type="number"
                      step="any"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md"
                      placeholder="19.451054"
                      min="-90"
                      max="90"
                      required
                    />
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1.5 font-medium">Valor entre -90 y 90</p>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                      Longitud *
                    </label>
                    <input
                      type="number"
                      step="any"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md"
                      placeholder="-70.697030"
                      min="-180"
                      max="180"
                      required
                    />
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-1.5 font-medium">Valor entre -180 y 180</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={getCurrentLocation}
                  className="inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary-50 to-primary-100 border-2 border-primary-200 text-primary-700 rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold shadow-sm hover:shadow-md hover:from-primary-100 hover:to-primary-200 transition-all duration-200 active:scale-95"
                >
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Obtener Ubicación Actual
                </button>
              </div>
            </div>

            {/* Services */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl sm:rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-gray-200/50">
              <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Servicios Ofrecidos</h2>

              <div className="space-y-4 sm:space-y-5">
                <div className="flex items-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border-2 border-gray-200 hover:border-primary-300 transition-all duration-200">
                  <input
                    id="offersDelivery"
                    name="offersDelivery"
                    type="checkbox"
                    checked={formData.offersDelivery}
                    onChange={handleCheckboxChange}
                    className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="offersDelivery" className="ml-3 sm:ml-4 block text-xs sm:text-sm font-bold text-gray-900 cursor-pointer">
                    Ofrezco servicio de delivery
                  </label>
                </div>

                <div className="flex items-center p-3 sm:p-4 bg-white rounded-xl sm:rounded-2xl border-2 border-gray-200 hover:border-primary-300 transition-all duration-200">
                  <input
                    id="offersKitchenService"
                    name="offersKitchenService"
                    type="checkbox"
                    checked={formData.offersKitchenService}
                    onChange={handleCheckboxChange}
                    className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="offersKitchenService" className="ml-3 sm:ml-4 block text-xs sm:text-sm font-bold text-gray-900 cursor-pointer">
                    Ofrezco servicio de cocina (preparación de alimentos)
                  </label>
                </div>

                {formData.offersDelivery && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 p-4 sm:p-5 bg-white rounded-xl sm:rounded-2xl border-2 border-primary-200 shadow-sm">
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Tarifa de Delivery (RD$)
                      </label>
                      <input
                        type="number"
                        name="deliveryFee"
                        value={formData.deliveryFee}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md"
                        placeholder="50"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                        Monto Mínimo de Pedido (RD$)
                      </label>
                      <input
                        type="number"
                        name="minOrderAmount"
                        value={formData.minOrderAmount}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 sm:py-3.5 rounded-xl sm:rounded-2xl border-2 border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200 text-sm sm:text-base font-medium bg-white shadow-sm hover:shadow-md"
                        placeholder="200"
                        min="0"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit */}
            <div className="pt-4 sm:pt-6 border-t-2 border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/store-owner/dashboard')}
                  className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-bold py-3 sm:py-3.5 px-6 rounded-xl sm:rounded-2xl border-2 border-gray-300 shadow-sm hover:shadow-md transition-all duration-200 active:scale-95 text-sm sm:text-base"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-bold py-3 sm:py-3.5 px-6 rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex items-center justify-center"
                >
                  <Save className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  {loading ? 'Creando...' : 'Crear Negocio'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CreateStore
